import { Component, OnInit, Input, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from 'environments/environment';
import { ProfileService, DistributorsService, StoreService, CoursesService, JobsService } from 'core/services';
import { Profile } from 'core/models/agent';
import { TranslateService } from '@ngx-translate/core';
import { Client } from 'core/models/client';
import { PlansService } from 'core/services/plans.service';
import {
  OWNER_COURSE_OPTIONS,
  SCOPES,
  BUTTON_ACTIONS_COURSES,
  BUTTON_ACTIONS_COURSES_SCORM
} from '../assets/constants';
import { Course } from 'core/models/course';
import { Plan } from 'core/models/plan';
import { Subscription } from 'rxjs';
import { ChangesAccessComponent, DownloadDocumentationComponent, SendDiplomaComponent } from './bulk-actions-courses';

@Component({
  selector: 'lms-courses',
  templateUrl: './courses.component.html',
  styleUrls: ['./courses.component.scss']
})
export class CoursesComponent implements OnInit {
  @ViewChild('changeAccess', { static: false })
  public changeAccess: ChangesAccessComponent;
  @ViewChild('sendDiploma', { static: false })
  public sendDiploma: SendDiplomaComponent;
  @ViewChild('downloadDocumentation', { static: false })
  public downloadDocumentation: DownloadDocumentationComponent;

  @Input() listId: any = 'COURSES_LIST';
  @Input() client: Client;
  // to determine component scope: 'courses' (route) or 'client'
  @Input() scope: string = SCOPES.courses;
  public catalogueId: string;
  public editorUrl: string = environment.AUTHORING_URL;

  public user: Profile;
  public courses: Course[] = [];
  public options: any = {
    filters: {},
    selectedColumns: [
      'title',
      'topic',
      'clientName',
      'duration',
      'credits',
      'languages',
      'accessMode',
      'shouldSendDiploma',
      'status'
    ],
    filterList: ['title', 'topic', 'duration', 'languages', 'accessMode', 'shouldSendDiploma', 'clientName']
  };
  public previewCampusProfileAction: string = 'courses.previewCampus';
  public ownerOptions: any[];
  public selectedOwner: string = 'all';
  private ownerConditions: any = {};
  private dataSource: any;
  public dataSubscription: Subscription;
  public defaultSendDiploma: boolean;

  public isSelectableCourses: boolean;
  public bulkActions: Array<object>;

  constructor(
    public activatedRoute: ActivatedRoute,
    public profileService: ProfileService,
    public distributorsService: DistributorsService,
    public plansService: PlansService,
    public translate: TranslateService,
    public elementRef: ElementRef,
    public coursesService: CoursesService,
    public jobsService: JobsService
  ) {
    this.profileService.getLoggedUser$().subscribe((user) => {
      this.user = user;
    });
  }

  public ngOnInit(): void {
    this.activatedRoute.data.subscribe((data) => {
      this.setCatalog(data['courses']);
      this.client = this.scope === SCOPES.courses ? data['client'] : this.client;
      if (this.scope === SCOPES.courses && !this.client) {
        this.options.selectedColumns = ['title', 'topic', 'clientName', 'duration', 'credits', 'status', 'languages'];
        this.options.filterList = ['title', 'topic', 'duration', 'languages', 'clientName'];
      }
      this.previewCampusProfileAction =
        this.scope === SCOPES.courses ? 'courses.previewCampus' : 'clients.previewCampus';

      const clientId = this.client ? this.client.id : this.user.clientId;
      const isExpert = this.user.role === 'expert';
      const isDistributor = this.user.role === 'distributor';
      const partnerId = (this.client && this.client.distributorId) || (isDistributor && this.user.distributorId);

      this.ownerConditions = {
        clientId,
        isExpert,
        isDistributor,
        editorClient: this.client && this.client.plan && this.client.plan.editorAccess,
        partner: null,
        editorPartner: false,
        onClientDetail: this.scope === SCOPES.clients
      };

      this.setOwnerOptions();

      if (partnerId) {
        this.distributorsService.getDistributor(partnerId).subscribe((partner) => {
          this.ownerConditions.partner = partner;
          if (partner.type === 'expert') {
            this.ownerConditions.isExpert = true;
            this.ownerConditions.editorPartner = true;
            this.setOwnerOptions();
          } else {
            this.plansService.getClientPlan(partner.clientId).then((plan: Plan) => {
              if (plan && plan.editorAccess) {
                this.ownerConditions.editorPartner = true;
              }
              this.setOwnerOptions();
            });
          }
        });
      }

      this.options.filters.selectedOwner = this.selectedOwner;
      this.options.filters.clientId = clientId;
      this.options.filters.partnerId = partnerId;

      //Actions button
      this.isSelectableCourses =
        this.profileService.canExecute('courses.bulk') && (this.scope === SCOPES.clients || !!this.client);
      this.bulkActions =
        this.isSelectableCourses && this.client
          ? this.client.scorm.enabled
            ? BUTTON_ACTIONS_COURSES_SCORM
            : BUTTON_ACTIONS_COURSES
          : [];
    });

    this.dataSubscription = StoreService.get(this.listId).subscribe((dataSource) => (this.dataSource = dataSource));
  }

  public ngOnDestroy(): void {
    this.dataSubscription.unsubscribe();
  }

  setCatalog(catalog: any = {}) {
    const { courses = [], defaultAccessMode } = catalog;
    this.courses = courses
      .filter((course) => !course.archived)
      .map((course) => {
        return { ...this.coursesService.getCachedCourseData(course.id), title: course.title || course.name };
      });
    this.options.defaultAccessMode = defaultAccessMode;
    this.catalogueId = catalog.id;
    this.defaultSendDiploma = catalog.configuration?.shouldSendDiploma;
  }

  setOwnerOptions() {
    this.ownerOptions = OWNER_COURSE_OPTIONS.filter((option) => option.isActiveOption(this.ownerConditions)).map(
      (option) => ({
        key: option.key,
        value: this.translate.instant(option.value)
      })
    );
  }

  campusPreview() {
    const clientId = this.client ? this.client.id : this.user.clientId;
    const campusUrl = `${environment.CAMPUS_URL}?trail=true&preview=true&clientId=${clientId}&lang=${this.user.lang}`;
    // eslint-disable-next-line
    window.open(campusUrl, '_blank');
  }

  async onChangeSelectedOwner(selectedOwner: string) {
    this.selectedOwner = selectedOwner;
    const filters = { ...this.dataSource.filters };
    filters.selectedOwner = selectedOwner;
    await this.dataSource.refreshData({ filters, page: 1 });
    StoreService.update(this.listId, this.dataSource);
  }

  coursesBulkActions(bulkAction) {
    const defaultAccessMode = this.options.defaultAccessMode;
    switch (bulkAction.action) {
      case 'changeAccess':
        this.changeAccess.openModal(bulkAction.selectedCourses, defaultAccessMode);
        break;
      case 'shouldSendDiploma':
        this.sendDiploma.openModal(bulkAction.selectedCourses, this.defaultSendDiploma);
        break;
      case 'downloadScormFile':
        this.downloadScormFile(bulkAction.selectedCourses);
        break;
      case 'downloadDocumentation':
        this.downloadDocumentation.openModal(bulkAction.selectedCourses);
        break;
    }
  }

  onConfirmBulkAction(jobData) {
    jobData.clientId = this.client ? this.client.id : null;
    this.coursesService.updateCoursesBulk(jobData).then((job: any) => {
      const jobSubscription = this.jobsService.manageJob(job.jobId, jobData.jobType).subscribe((jobStatus) => {
        if (jobStatus.status === 'finished' || jobStatus.status === 'fatal-error') {
          jobSubscription.unsubscribe();
          this.coursesService.loadClientCatalogueCache(this.client.id, true, true).then((catalog) => {
            this.setCatalog(catalog);
          });
        }
      });
    });
  }

  downloadScormFile(selectedCourses) {
    const jobData = {
      jobType: this.jobsService.jobTypes.DOWNLOAD_COURSE_SCORM_BULK,
      courses: [...selectedCourses.keys()].map((id) => ({ id })),
      lang: this.client.lang
    };
    this.onConfirmBulkAction(jobData);
  }
}
